/* Announcement bar — autoplay carousel, no dependencies.
 * Respects prefers-reduced-motion and tab-visibility (pauses when hidden). */
class AnnouncementBar {
  constructor(wrapper) {
    this.wrapper = wrapper;
    this.slides = [...wrapper.querySelectorAll('.announcement-bar__slide')];
    this.prev = wrapper.querySelector('.announcement-bar__arrow--prev');
    this.next = wrapper.querySelector('.announcement-bar__arrow--next');
    this.current = 0;
    this.timer = null;
    this.autoplay = wrapper.dataset.autoplay === 'true';
    this.speed = (parseInt(wrapper.dataset.speed, 10) || 5) * 1000;

    if (this.slides.length <= 1) return;

    this.prev?.addEventListener('click', () => this.go(this.current - 1));
    this.next?.addEventListener('click', () => this.go(this.current + 1));
    if (this.autoplay && !matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.start();
      document.addEventListener('visibilitychange', () => (document.hidden ? this.stop() : this.start()));
      this.wrapper.addEventListener('focusin', () => this.stop());
      this.wrapper.addEventListener('focusout', () => this.start());
      this.wrapper.addEventListener('pointerenter', () => this.stop());
      this.wrapper.addEventListener('pointerleave', () => this.start());
    }
  }

  go(index) {
    const len = this.slides.length;
    this.current = (index + len) % len;
    this.slides.forEach((slide, i) => slide.classList.toggle('is-active', i === this.current));
  }
  start() { this.stop(); this.timer = setInterval(() => this.go(this.current + 1), this.speed); }
  stop() { if (this.timer) clearInterval(this.timer); this.timer = null; }
}

document.querySelectorAll('.announcement-bar__wrapper').forEach((el) => new AnnouncementBar(el));

/* ============================================================
 * <lion-announcement-bar> — handles dismiss (X) with optional TTL
 * stored in localStorage. Hides the bar early on subsequent
 * visits if the user explicitly closed it.
 * ============================================================ */
class AnnouncementBarElement extends HTMLElement {
  constructor() {
    super();
    this.storageKey = this.dataset.closeStorage;
    this.ttlDays = parseInt(this.dataset.closeTtl, 10) || 0;
    this.closeBtn = this.querySelector('[data-announcement-close]');
  }

  connectedCallback() {
    if (this.storageKey && this._shouldStayHidden()) {
      this.style.display = 'none';
      return;
    }
    this.closeBtn?.addEventListener('click', () => this._dismiss());
  }

  _shouldStayHidden() {
    try {
      const raw = localStorage.getItem(this.storageKey);
      if (!raw) return false;
      const closedAt = parseInt(raw, 10);
      if (!closedAt) return false;
      // 0 days means "remember for this session" — but localStorage is persistent;
      // when ttlDays is 0 we honour the flag forever (until manually cleared).
      if (this.ttlDays === 0) return true;
      const expires = closedAt + this.ttlDays * 86400 * 1000;
      return Date.now() < expires;
    } catch {
      return false;
    }
  }

  _dismiss() {
    this.style.display = 'none';
    try {
      if (this.storageKey) localStorage.setItem(this.storageKey, String(Date.now()));
    } catch {}
  }
}

if (!customElements.get('lion-announcement-bar')) {
  customElements.define('lion-announcement-bar', AnnouncementBarElement);
}

/* ============================================================
 * <lion-free-shipping-bar>
 * Live progress to the merchant's free-shipping threshold.
 * Subscribes to cart-update events and refreshes from /cart.js.
 * Theme Store compliant: 100% real values, no fabrication.
 * ============================================================ */
class FreeShippingBar extends HTMLElement {
  constructor() {
    super();
    this.threshold = parseInt(this.dataset.threshold, 10) || 0;
    this.msgProgress = this.dataset.messageProgress || '';
    this.msgUnlocked = this.dataset.messageUnlocked || '';
    this.msgEmpty = this.dataset.messageEmpty || '';
    this.textEl = this.querySelector('[data-shipping-text]');
    this.fillEl = this.querySelector('[data-shipping-progress-fill]');
    this.progressEl = this.querySelector('[data-shipping-progress]');

    if (this.threshold <= 0) return;

    this.refresh = this.refresh.bind(this);
    this.cartUnsub = window.subscribe?.(window.PUB_SUB_EVENTS?.cartUpdate, this.refresh);
  }

  disconnectedCallback() {
    this.cartUnsub?.();
  }

  async refresh() {
    try {
      const res = await fetch('/cart.js');
      if (!res.ok) return;
      const cart = await res.json();
      this.update(cart.total_price, cart.item_count);
    } catch {
      /* silent */
    }
  }

  update(totalCents, itemCount) {
    const remaining = this.threshold - totalCents;
    const pct = totalCents >= this.threshold
      ? 100
      : Math.max(0, Math.min(100, Math.round((totalCents / this.threshold) * 100)));

    if (this.fillEl) this.fillEl.style.width = `${pct}%`;
    if (this.progressEl) this.progressEl.setAttribute('aria-valuenow', String(pct));

    if (!this.textEl) return;

    if (itemCount === 0) {
      this.textEl.textContent = this.msgEmpty
        .replace(/\[amount\]/g, this.formatMoney(this.threshold))
        .replace(/\{\{\s*amount\s*\}\}/g, this.formatMoney(this.threshold));
      this.removeAttribute('data-state');
      return;
    }

    if (remaining <= 0) {
      this.textEl.textContent = this.msgUnlocked;
      this.dataset.state = 'unlocked';
      return;
    }

    this.textEl.textContent = this.msgProgress
      .replace(/\[remaining\]/g, this.formatMoney(remaining))
      .replace(/\{\{\s*remaining\s*\}\}/g, this.formatMoney(remaining));
    this.dataset.state = 'progress';
  }

  formatMoney(cents) {
    const value = (cents / 100).toFixed(2);

    // Preferred path: use the merchant's shop.money_format (set in theme.liquid).
    // Supports {{amount}}, {{amount_no_decimals}}, {{amount_with_comma_separator}},
    // {{amount_no_decimals_with_comma_separator}} — the four common Shopify tokens.
    const fmt = window.moneyFormat;
    if (fmt && typeof fmt === 'string') {
      const intPart = Math.round(cents / 100).toString();
      const valueComma = value.replace('.', ',');
      const intComma = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
      return fmt
        .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, intComma)
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, valueComma)
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, intPart)
        .replace(/\{\{\s*amount\s*\}\}/g, value);
    }

    // Locale-aware fallback (used only if window.moneyFormat is unset, which
    // shouldn't happen in production — theme.liquid always assigns it).
    const currency = window.shopCurrency || 'USD';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, {
        style: 'currency',
        currency,
      }).format(cents / 100);
    } catch {
      return `${currency} ${value}`;
    }
  }
}

if (!customElements.get('lion-free-shipping-bar')) {
  customElements.define('lion-free-shipping-bar', FreeShippingBar);
}
